import React, { useEffect, useState } from "react";
import Multiselect from "multiselect-react-dropdown";

import Constants from "../../constants";
import HttpManager from "../../services/HttpManager";
import { AdvisorCandidateDTO, AdvisorSelection } from "../../models/EnrollmentHistoryDTO";

export interface AdvisorsPickerProps {
	campusId: number;
	value: AdvisorSelection[] | null;
	onChange: (advisors: AdvisorSelection[] | null) => void;
	disabled?: boolean;
	invalid?: boolean;
	/** false = no se ofrece "Ninguno": en el alta de matrícula siempre hubo alguien que atendió (Esteban, 2026-09-22). */
	allowNone?: boolean;
}

/**
 * Picks the people credited with a sale (asesoras).
 * Candidates come from the API: enabled users holding a sales/admissions role in the campus.
 * The field is mandatory, so it has three states (Esteban, 2026-09-11):
 * null = nothing chosen yet (blocks the save), [] = "Ninguno" chosen on purpose, [...] = advisors.
 */
const AdvisorsPicker: React.FC<AdvisorsPickerProps> = ({ campusId, value, onChange, disabled, invalid, allowNone = true }) => {
	const http = HttpManager.getInstance();
	const [candidates, setCandidates] = useState<AdvisorCandidateDTO[]>([]);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [reload, setReload] = useState(0);

	//A dropped connection used to leave the list empty for good (the request never reached the server,
	//Esteban 2026-09-26): retry on our own a few times, then offer "Reintentar".
	useEffect(() => {
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const esperas = [0, 1500, 4000, 8000];
		const intentar = (n: number) => {
			http.enrollmentService.getAdvisorCandidates(campusId)
				.then((response) => {
					if (cancelled) { return; }
					setCandidates(Array.isArray(response.data) ? response.data : []);
					setLoadError(null);
				})
				.catch((error) => {
					if (cancelled) { return; }
					console.error("Asesoras: no se pudo cargar la lista (intento " + (n + 1) + ")", error);
					if (n + 1 < esperas.length) {
						timer = setTimeout(() => intentar(n + 1), esperas[n + 1]);
					} else {
						setLoadError(Constants.Common.advisorsLoadError);
					}
				});
		};
		setLoadError(null);
		intentar(0);
		return () => { cancelled = true; if (timer) { clearTimeout(timer); } };
	}, [campusId, reload]);

	const NONE_ID = "__none__";
	const noneOption: AdvisorCandidateDTO = { id: NONE_ID, name: Constants.Common.noneOption, email: null, roles: [] };
	const options = allowNone ? [noneOption, ...candidates] : candidates;
	// "Ninguno" se muestra como una selección más, para que se vea que fue una decisión.
	const selectedCandidates = value === null
		? []
		: (value.length < 1 ? (allowNone ? [noneOption] : []) : candidates.filter((candidate) => value.some((advisor) => advisor.userId === candidate.id)));

	const handleSelect = (selected: AdvisorCandidateDTO[]) => {
		const conNinguno = selected.some((candidate) => candidate.id === NONE_ID);
		const otras = selected.filter((candidate) => candidate.id !== NONE_ID);
		//Si ya estaba en "Ninguno" y ahora eligen a alguien, gana la asesora; si tenían
		//asesoras y eligen "Ninguno", gana "Ninguno".
		if (conNinguno && !(value !== null && value.length < 1 && otras.length > 0)) {
			onChange([]);
			return;
		}
		if (otras.length < 1) {
			//Quitar todo deja el campo sin responder: vuelve a quedar pendiente.
			onChange(null);
			return;
		}
		onChange(otras.map((candidate) => {
			const existing = (value ?? []).find((advisor) => advisor.userId === candidate.id);
			return existing ?? { userId: candidate.id, name: candidate.name, sharePercentage: null };
		}));
	};

	const pendiente = value === null;

	return (
		<div className="advisorsPicker">
			<label style={{ position: "relative", display: "block" }}>
				{Constants.Common.advisors} <span style={{ color: "var(--wa-rojo)" }}>*</span>
			</label>
			{loadError
				? <div className="text-danger" style={{ fontSize: 13 }}>
					{loadError}{" "}
					<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 13, verticalAlign: "baseline" }}
						onClick={() => setReload((n) => n + 1)}>Reintentar</button>
				</div>
				: ""}
			<div style={invalid && pendiente ? { border: "1px solid var(--wa-rojo)", borderRadius: 6 } : undefined}>
				<Multiselect
					options={options}
					displayValue="name"
					selectedValues={selectedCandidates}
					onSelect={handleSelect}
					onRemove={handleSelect}
					disable={disabled}
					placeholder={selectedCandidates.length < 1 ? Constants.Common.selectAdvisors : ""}
					emptyRecordMsg={Constants.Common.noMoreOptions}
					avoidHighlightFirstOption
				/>
			</div>
			<div style={{ fontSize: 13, color: pendiente ? "var(--wa-rojo)" : "var(--wa-apagado)", marginTop: 4 }}>
				{pendiente
					? (allowNone ? Constants.Common.advisorsRequired : "Elija a la asesora o asesoras que atendieron al alumno.")
					: (value.length < 1
						? (allowNone ? Constants.Common.noneOption + " · " + Constants.Common.noAdvisorsHint : "Elija a la asesora o asesoras que atendieron al alumno.")
						: Constants.Common.advisorsSelectedHint)}
			</div>
		</div>
	);
};

export default AdvisorsPicker;
